import React from 'react'

import AnimationLink from './Link'
import LetsTalkCta from './LetsTalkCta'
import CookieActions from './CookieActions'
import { getFooterContent } from '../constants/footerContent'

import logLogoWhite from '../assets/images/footer/log-white.svg'
import lisboa2020Logo from '../assets/images/footer/lisboa-2020.svg'
import portugal2020Logo from '../assets/images/footer/portugal-2020.svg'
import euLogo from '../assets/images/footer/eu.svg'
import top5Logo from '../assets/images/footer/log-top5-2025.png'
import top10Logo from '../assets/images/footer/log-top10-2025.png'
import pmeLogo from '../assets/images/footer/pme_lider_25.png'
import googleCloudLogo from '../assets/images/footer/google-cloud.png'
import greatPlaceToWorkLogo from '../assets/images/footer/SELO_MLPT2026.png'
import linkedinIcon from '../assets/images/footer/linkedin.svg'

const LOGO_WIDTH = 78
const LOGO_HEIGHT = 40
const CERT_LOGO_WIDTH = 80
const CERT_LOGO_HEIGHT = 30
const LINKEDIN_ICON_SIZE = 24

const FOOTER_FUNDING_LOGOS = [
  { src: lisboa2020Logo, alt: 'Lisboa 2020 Logo', title: 'Lisboa 2020' },
  { src: portugal2020Logo, alt: 'Portugal 2020 Logo', title: 'Portugal 2020' },
  { src: euLogo, alt: 'European Union Logo', title: 'European Union' },
]

const FOOTER_AWARD_LOGOS = [
  {
    href: 'https://www.greatplacetowork.pt/empresas-certified/tecnologias-da-informacao/log',
    src: greatPlaceToWorkLogo,
    alt: 'Great Place To Work Logo',
    title: 'Great Place To Work',
    className: 'site-footer__award-logo site-footer__award-logo--gptw',
    width: 120,
    height: 80,
  },
  {
    href: 'https://scoring.pt/empresas-certificadas/log-oscon',
    src: top5Logo,
    alt: 'Scoring Top 5% Logo',
    title: 'Scoring Top 5%',
    className: 'site-footer__award-logo site-footer__award-logo--top5',
    width: 80,
    height: 80,
  },
  {
    href: 'https://scoring.pt/empresas-certificadas/log-oscon',
    src: top10Logo,
    alt: 'Scoring Top 10 Logo',
    title: 'Scoring Top 10',
    className: 'site-footer__award-logo site-footer__award-logo--top10',
    width: 85,
    height: 85,
  },
  {
    src: pmeLogo,
    alt: "PME líder '25 Logo",
    title: "PME líder '25",
    className: 'site-footer__award-logo site-footer__award-logo--pme',
    width: 60,
    height: 70,
  },
  {
    href: 'https://cloud.google.com/find-a-partner/partner/log',
    src: googleCloudLogo,
    alt: 'Google Cloud Partner Logo',
    title: 'Google Cloud Partner',
    className: 'site-footer__award-logo site-footer__award-logo--google',
    width: 80,
    height: 80,
  },
]

const FooterFundingLogos = () => (
  <div className="site-footer__badges-funding" aria-label="Funding programmes">
    {FOOTER_FUNDING_LOGOS.map(logo => (
      <img
        key={logo.title}
        src={logo.src}
        alt={logo.alt}
        title={logo.title}
        width={CERT_LOGO_WIDTH}
        height={CERT_LOGO_HEIGHT}
        loading="lazy"
        decoding="async"
        className="site-footer__funding-logo"
      />
    ))}
  </div>
)

const FooterAwardLogo = ({ logo }) => {
  const image = (
    <img
      src={logo.src}
      alt={logo.alt}
      title={logo.title}
      width={logo.width}
      height={logo.height}
      loading="lazy"
      decoding="async"
      className={logo.className}
    />
  )

  if (!logo.href) {
    return image
  }

  return (
    <a
      href={logo.href}
      className="site-footer__award-link"
      target="_blank"
      rel="noopener noreferrer"
      aria-label={logo.title}
    >
      {image}
    </a>
  )
}

const FooterAwardLogos = () => (
  <div className="site-footer__badges-awards" aria-label="Certifications and partnerships">
    {FOOTER_AWARD_LOGOS.map(logo => (
      <FooterAwardLogo key={logo.title} logo={logo} />
    ))}
  </div>
)

const Footer = ({ lang }) => {
  const isEn = lang === 'en'
  const content = getFooterContent(isEn)
  const year = new Date().getFullYear()

  return (
    <footer id="footer" className="site-footer">
      <div className="site-footer__shell">
        <div className="site-footer__inner">
          <div
            className="site-footer__cta"
            role="region"
            aria-label={isEn ? 'Contact call to action' : 'Chamada para contacto'}
          >
            <div className="site-footer__cta-text">
              <h2 className="site-footer__cta-title">{content.cta.title}</h2>
              <p className="site-footer__cta-description">{content.cta.description}</p>
            </div>
            <div className="site-footer__cta-action">
              <LetsTalkCta
                isEn={isEn}
                hex="#333333"
                tone="onDark"
                paintDrip
                to={content.cta.linkTo}
                label={content.cta.linkLabel}
                className="site-footer__cta-link"
                aria-label={isEn ? "Let's talk — contact us" : 'Contacte-nos'}
              />
            </div>
          </div>

          <hr className="site-footer__divider" />

          <div className="site-footer__main">
            <div className="site-footer__brand">
              <AnimationLink
                to={isEn ? '/en/' : '/'}
                hex="#333333"
                paintDrip={false}
                aria-label={isEn ? 'log home' : 'log início'}
              >
                <img
                  src={logLogoWhite}
                  alt=""
                  width={LOGO_WIDTH}
                  height={LOGO_HEIGHT}
                  loading="lazy"
                  decoding="async"
                  className="site-footer__logo"
                />
              </AnimationLink>
            </div>

            <div className="site-footer__columns">
              <nav className="site-footer__nav-services" aria-label={content.services.label}>
                <p className="site-footer__nav-heading site-footer__nav-heading--static">
                  {content.services.label}
                </p>
                <ul className="site-footer__nav-list site-footer__nav-list--services">
                  {content.services.items.map(item => (
                    <li key={item.href}>
                      <AnimationLink
                        to={item.href}
                        hex="#333333"
                        paintDrip={false}
                        className="site-footer__nav-link site-footer__nav-link--service"
                      >
                        {item.label}
                      </AnimationLink>
                    </li>
                  ))}
                </ul>
              </nav>

              <nav
                className="site-footer__nav-primary"
                aria-label={isEn ? 'Main navigation' : 'Navegação principal'}
              >
                <ul className="site-footer__nav-primary-list">
                  {content.primaryLinks.map(link => (
                    <li key={link.href} className="site-footer__nav-primary-item">
                      <AnimationLink
                        to={link.href}
                        hex="#333333"
                        paintDrip={false}
                        className="site-footer__nav-heading site-footer__nav-heading--link"
                      >
                        {link.label}
                      </AnimationLink>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="site-footer__aside">
                <div className="site-footer__contact">
                  <p className="site-footer__contact-email">
                    <a href={content.contact.emailHref} className="site-footer__contact-link">
                      {content.contact.email}
                    </a>
                  </p>
                  <p className="site-footer__contact-address">
                    {content.contact.addressLine1}
                    <br />
                    {content.contact.addressLine2}
                  </p>
                  <a
                    href={content.contact.linkedin.href}
                    className="site-footer__social"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={content.contact.linkedin.label}
                  >
                    <img
                      src={linkedinIcon}
                      alt=""
                      width={LINKEDIN_ICON_SIZE}
                      height={LINKEDIN_ICON_SIZE}
                      decoding="async"
                    />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="site-footer__badges">
            <FooterFundingLogos />
            <FooterAwardLogos />
          </div>

          <hr className="site-footer__divider" />

          <div className="site-footer__bottom">
            <p className="site-footer__copyright">
              {year} &copy; {content.legal.copyright}
            </p>
            <ul className="site-footer__legal">
              <li>
                <button
                  type="button"
                  className="site-footer__legal-link"
                  onClick={() => CookieActions.showCookieModal()}
                >
                  {content.legal.cookieLabel}
                </button>
              </li>
              <li>
                <a href={content.legal.esg.href} className="site-footer__legal-link">
                  {content.legal.esg.label}
                </a>
              </li>
              <li>
                <AnimationLink
                  to={content.legal.logAi.href}
                  hex="#333333"
                  paintDrip={false}
                  className="site-footer__legal-link"
                >
                  {content.legal.logAi.label}
                </AnimationLink>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
